import DOMPurify from 'dompurify';
import { isString } from 'lodash';

// Rich text is stored as markdown or HTML and ends up in [innerHTML] and the clipboard, so any HTML is sanitised on the
// way into the editor, on the way out of it, and before it's displayed. Tiptap's schema already drops unknown
// attributes; this guards stored content and any future extension that lets raw HTML through.
const FORBID_TAGS = ['style', 'form', 'button', 'textarea', 'select', 'option', 'meta', 'link', 'base'];

// HTML saved by the editor always starts with an element (<p>, <h2>, <ul>…). Markdown that merely contains angle
// brackets, such as "<39.5", an autolink like <https://joii.vet> or an inline <br>, is still markdown.
const STARTS_WITH_ELEMENT = /^<([a-z][a-z0-9]*)(\s[^>]*)?\/?>/i;

let purifier: typeof DOMPurify | undefined;

// A dedicated instance, so the checkbox rule doesn't change DOMPurify for the rest of the app
function getPurifier(): typeof DOMPurify {
  if (!purifier) {
    purifier = DOMPurify(window);
    // Task lists render a checkbox per item; keep those (read-only) and drop every other kind of input
    purifier.addHook('uponSanitizeElement', (node, data) => {
      if (data.tagName !== 'input' || !(node instanceof Element)) return;
      if (node.getAttribute('type') === 'checkbox') {
        node.setAttribute('disabled', '');
      } else {
        node.parentNode?.removeChild(node);
      }
    });
  }
  return purifier;
}

export function isRichTextHTML(value: string): boolean {
  return isString(value) && STARTS_WITH_ELEMENT.test(value.trim());
}

export function sanitizeRichTextHTML(html: string): string {
  if (!isString(html) || !html) return '';
  return getPurifier().sanitize(html, { FORBID_TAGS, ADD_ATTR: ['target', 'rel'] });
}
